/**
 * Operations → Rejected.
 *
 * The last terminal book in the status-coverage spine (ADR-0048) — leads no
 * Handling Firm accepted (Lead Status = "Rejected"; routing exhausted, never
 * placed). Lead-driven and terminal: no clock, nothing acted on, no anomaly
 * concept (every row is a resolved lead).
 *
 * Scale makes this surface different from its sibling books. Rejected is the
 * largest in the system (1000+), so it is NOT rendered flat. v1 shows a recent
 * window — the most recent N by Creation Date — as a portal-style table; a
 * search box over the full archive is a deferred pass. When the window is full
 * the header says "N most recent" so it is never mistaken for the whole book.
 *
 * LAYOUT (portal-imprint, D1=hybrid): this terminal/census book renders as a
 * portal-style <Table> — the first consumer of the new Table primitive (the
 * tracer that proves it). Active books (Under Review book, Accepted book) keep their cards for
 * the time-bars; census books like this one are tables. Each row opens the
 * shared CaseDetailPanel (portal parity: a row is clickable → detail).
 *
 * `RejectedCard` (below) is unchanged — The Surface still renders rejected
 * matters as cards in its mixed-phase overlay.
 *
 * Read-only. Matters enter (reject) upstream; the surface re-derives on poll.
 *
 * Spec: engineering/command-center/docs/port-notes/rejected.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import type { RejectedCase } from "@command-center/shared";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { rejectedToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtDate } from "@/lib/format";
import { useRejected } from "@/lib/use-rejected";

export function RejectedPage() {
  const rejected = useRejected();
  const [query, setQuery] = useState("");
  // The case-detail side panel is lifted to the page so a row stays open while
  // you read it (portal parity: clicking a row opens the matter detail).
  const [detail, setDetail] = useState<CaseDetail | null>(null);

  if (rejected.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (rejected.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="rejected-error">
        Couldn't load Rejected. It will retry automatically.
      </div>
    );
  }

  const searching = query.trim() !== "";

  const cases = rejected.data?.cases ?? [];
  const windowSize = rejected.data?.windowSize ?? 0;
  // A full window means the book is larger than what's shown — say so rather
  // than imply this is the whole archive.
  const capped = cases.length >= windowSize && windowSize > 0;

  const visibleCases = cases.filter((c) => matchesQuery(c.client, query));

  const countSpan = (
    <span className="text-sm text-plane-muted" data-testid="rejected-count">
      {cases.length === 0
        ? ""
        : capped
          ? `${cases.length} most recent`
          : `${cases.length} rejected case${cases.length === 1 ? "" : "s"}`}
    </span>
  );

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="rejected">
      <BookHeading phaseKey="rejected">{countSpan}</BookHeading>

      <SearchBar value={query} onChange={setQuery} testId="rejected-search" />

      {visibleCases.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="rejected-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "No rejected cases."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Referred by</TableHead>
                <TableHead>Case type</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Firms routed</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>
                  <span className="sr-only">Watch</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleCases.map((c) => (
                <RejectedRow
                  key={c.id}
                  rejectedCase={c}
                  onOpenDetail={() => setDetail(rejectedToDetail(c))}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="rejected-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** One rejected matter as a portal-style table row. Columns mirror the card's
 *  fields (client · OF · type · state · firms routed · created); the whole row
 *  opens the detail panel. The trailing cell carries the SurfaceTrigger (the
 *  native watch/to-do control, preserved on every book) — its click is stopped
 *  so it doesn't also open the panel. */
function RejectedRow({
  rejectedCase: c,
  onOpenDetail,
}: {
  rejectedCase: RejectedCase;
  onOpenDetail: () => void;
}) {
  return (
    <TableRow onClick={onOpenDetail} data-testid="rejected-card">
      <TableCell className="font-medium" data-testid="rejected-client">
        {c.client ?? "—"}
      </TableCell>
      <TableCell
        className={c.originatingFirmName ? undefined : "text-muted-foreground"}
        data-testid="rejected-of-firm"
      >
        {c.originatingFirmName ?? "—"}
      </TableCell>
      <TableCell data-testid="rejected-casetype">{c.caseType ?? "—"}</TableCell>
      <TableCell data-testid="rejected-state">{c.caseState ?? "—"}</TableCell>
      <TableCell className="tabular-nums" data-testid="rejected-firms">
        {c.firmsRouted} firm{c.firmsRouted === 1 ? "" : "s"} routed
      </TableCell>
      <TableCell
        className="tabular-nums text-muted-foreground"
        data-testid="rejected-created"
      >
        {c.createdDate !== null ? fmtDate(c.createdDate, { year: true }) : "—"}
      </TableCell>
      <TableCell
        className="w-0 text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <SurfaceTrigger leadId={c.id} />
      </TableCell>
    </TableRow>
  );
}

/** One rejected matter as a card — used by The Surface's mixed-phase overlay.
 *  client + date created up top, then the originating firm, and a meta line of
 *  case type · state · firms routed. */
export function RejectedCard({
  rejectedCase: c,
  onOpenDetail,
}: {
  rejectedCase: RejectedCase;
  /** When provided (on The Surface), a "Case detail →" link opens the panel. */
  onOpenDetail?: () => void;
}) {
  return (
    <li className="rounded-lg border bg-card text-card-foreground px-4 py-3" data-testid="rejected-card">
      {/* Top line: who on the left, date created on the right. */}
      <div className="flex items-start gap-3">
        <span
          className="min-w-0 flex-1 text-sm font-medium"
          data-testid="rejected-client"
        >
          {c.client ?? "—"}
        </span>
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums text-muted-foreground"
            data-testid="rejected-created"
          >
            {c.createdDate !== null ? fmtDate(c.createdDate, { year: true }) : "—"}
          </span>
          <span className="block text-[11px] text-muted-foreground">created</span>
        </span>
        <SurfaceTrigger leadId={c.id} />
      </div>

      {/* The originating firm — the source that referred the matter. There is
          no handling firm: a rejected matter was never placed. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="rejected-of-firm"
        >
          <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            OF
          </span>
          <span
            className={c.originatingFirmName ? "font-medium" : "text-muted-foreground"}
          >
            {c.originatingFirmName ?? "—"}
          </span>
        </span>
      </div>

      {/* Meta line: what kind of matter, where, and how many firms passed on it
          before it was rejected (1–3, capped by the reroute rule). */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span data-testid="rejected-casetype">{c.caseType ?? "—"}</span>
        <span aria-hidden>·</span>
        <span data-testid="rejected-state">{c.caseState ?? "—"}</span>
        <span aria-hidden>·</span>
        <span className="tabular-nums" data-testid="rejected-firms">
          {c.firmsRouted} firm{c.firmsRouted === 1 ? "" : "s"} routed
        </span>
      </div>

      {onOpenDetail ? (
        <button
          type="button"
          onClick={onOpenDetail}
          className="mt-2 text-xs font-medium text-interactive hover:underline"
          data-testid="rejected-open-detail"
        >
          Case detail →
        </button>
      ) : null}
    </li>
  );
}

